import {
  Context,
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { UserProfile } from '../../types/auth/user';
import { IUserContext } from './types';
import BaseServerInteractionService from '../../serverInteraction/services/BaseServerInteractionService';
import { AuthProps, LoginProps } from '../../types/auth/auth';
import { AUTH_LOGIN, AUTH_REGISTER } from '../../serverInteraction/services/endpointsConstants';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCompany, setUserPackage } from '../../store/packages/packagesSlice';
import { useDispatch } from 'react-redux';
import { PackageProps } from '../../types/packages/PackageProps';
import { initialState as initialStatePackage } from '../../store/packages/constants';
import { RegisterProfile, RegisterResponse } from '../../types/auth/register';
import { authStorage, buildUserProfile } from '../../serverInteraction/services/authStorage';
import { getApiErrorMessage, parseApiJson } from '../../serverInteraction/services/apiDataUtils';
import { OrganisationResponse } from '../../types/organisations/organisation';
import { organisationService } from '../../serverInteraction/services/organisationService';
import { UserSummaryResponse } from '../../types/organisations/userSummary';
import { canManageOrganisationMembers } from '../../utils/auth/roles';
import {
  LOGIN_RATE_LIMIT_COOLDOWN_SECONDS,
  REGISTRATION_RATE_LIMIT_COOLDOWN_SECONDS,
  getRateLimitRequestUrl,
  isRateLimitError,
} from '../../serverInteraction/services/rateLimit';

const UserContext = createContext<IUserContext | undefined>(undefined);
const DEFAULT_AUTHENTICATED_ROUTE = '/profile';

type Props = PropsWithChildren;

const UserProvider = ({ children }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const genericErrorMessage = String(t('somethingWentWrong'));
  const loginRateLimitMessage = String(
    t('authLoginRateLimitMessage') || 'Too many attempts. Wait a minute and try again.',
  );
  const registerRateLimitMessage = String(
    t('authRegisterRateLimitMessage') ||
      'Too many registration attempts. Please try later or contact support.',
  );

  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [organisation, setOrganisation] = useState<OrganisationResponse | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const initSession = async () => {
      const savedToken = authStorage.getToken();
      const savedRefreshToken = authStorage.getRefreshToken();
      const savedUser = authStorage.getUser();
      const savedOrganisation = authStorage.getOrganisation();

      if (savedUser && savedToken && savedRefreshToken) {
        setUser(savedUser);
        setToken(savedToken);
        setRefreshToken(savedRefreshToken);
        setOrganisation(savedOrganisation);

        try {
          const syncedUser = await syncSessionContext(savedUser);

          if (!isMounted) {
            return;
          }

          const newPackage = initUserPackage(syncedUser?.company ?? savedUser.company);
          navigate(getNavigateTo(newPackage.company, newPackage.document, location.pathname));
        } catch {
          if (!isMounted) {
            return;
          }

          const newPackage = initUserPackage(savedUser.company);
          navigate(getNavigateTo(newPackage.company, newPackage.document, location.pathname));
        }
      }

      if (isMounted) {
        setIsReady(true);
      }
    };

    void initSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const getHttpStatus = (error: unknown): number | undefined => {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'status' in error.response &&
      typeof error.response.status === 'number'
    ) {
      return error.response.status;
    }

    return undefined;
  };

  const findCurrentUserSummary = useCallback((baseUser: UserProfile, users: UserSummaryResponse[]) => {
    const baseEmail = baseUser.email.toLowerCase();
    const baseUsername = baseUser.username.toLowerCase();

    return users.find((organisationUser) => {
      const candidateEmail = organisationUser.email.toLowerCase();
      return candidateEmail === baseEmail || candidateEmail === baseUsername;
    });
  }, []);

  const syncSessionContext = useCallback(
    async (baseUser?: UserProfile | null) => {
      const currentUser = baseUser ?? authStorage.getUser() ?? user;

      if (!currentUser) {
        return null;
      }

      const currentOrganisation = await organisationService.getCurrentOrganisation();
      setOrganisation(currentOrganisation);
      authStorage.setOrganisation(currentOrganisation);

      let syncedUser = currentUser;

      try {
        const organisationUsers = await organisationService.getOrganisationUsers();
        const currentUserSummary = findCurrentUserSummary(currentUser, organisationUsers);

        syncedUser = currentUserSummary
          ? {
              ...currentUser,
              id: currentUserSummary.id,
              role: currentUserSummary.role,
            }
          : {
              ...currentUser,
              role: currentUser.role ?? 'ORG_ADMIN',
            };
      } catch (sessionError: unknown) {
        if (getHttpStatus(sessionError) === 403) {
          syncedUser = {
            ...currentUser,
            role: 'ORG_MEMBER',
          };
        } else {
          throw sessionError;
        }
      }

      setUser(syncedUser);
      authStorage.setUser(syncedUser);

      return syncedUser;
    },
    [findCurrentUserSummary, user],
  );

  const completeLogin = useCallback(async (authData: AuthProps, _from: string) => {
    authStorage.setAuth(authData);

    const userObj = buildUserProfile(authData);

    setToken(authData.token);
    setRefreshToken(authData.refreshToken);
    setUser(userObj);
    setError(null);

    let syncedUser = userObj;

    try {
      const currentUser = await syncSessionContext(userObj);
      if (currentUser) {
        syncedUser = currentUser;
      }
    } catch {
      authStorage.setUser(userObj);
    }

    initUserPackage(syncedUser.company);
    navigate(DEFAULT_AUTHENTICATED_ROUTE, { replace: true });
  }, [navigate, syncSessionContext]);

  const login = async (loginData: LoginProps, from: string) => {
    try {
      setIsFetching(true);
      setError(null);
      const res = await BaseServerInteractionService.Post<AuthProps | string>(`/${AUTH_LOGIN}`, loginData);
      const authData = parseApiJson<AuthProps>(res);

      if (!authData?.token || !authData.refreshToken) {
        throw new Error(genericErrorMessage);
      }

      await completeLogin(authData, from);
      return {
        success: true,
      };
    } catch (error: unknown) {
      if (isRateLimitError(error)) {
        setError(loginRateLimitMessage);
        return {
          success: false,
          rateLimited: true,
          cooldownSeconds: LOGIN_RATE_LIMIT_COOLDOWN_SECONDS,
        };
      }

      setError(getApiErrorMessage(error, genericErrorMessage));
      return {
        success: false,
      };
    } finally {
      setIsFetching(false);
    }
  };

  const reg = async (regData: RegisterProfile, from: string) => {
    try {
      setIsFetching(true);
      setError(null);
      const registerResponse = await BaseServerInteractionService.Post<RegisterResponse | string>(
        `/${AUTH_REGISTER}`,
        regData,
      );
      const registeredUser = parseApiJson<RegisterResponse>(registerResponse);

      if (!registeredUser) {
        throw new Error(genericErrorMessage);
      }

      const loginResponse = await BaseServerInteractionService.Post<AuthProps | string>(
        `/${AUTH_LOGIN}`,
        {
          email: regData.email,
          password: regData.password,
        },
      );
      const authData = parseApiJson<AuthProps>(loginResponse);

      if (!authData?.token || !authData.refreshToken) {
        throw new Error(genericErrorMessage);
      }

      await completeLogin(authData, from);
      return {
        success: true,
      };
    } catch (error: unknown) {
      if (isRateLimitError(error)) {
        const rateLimitRequestUrl = getRateLimitRequestUrl(error) || '';
        const isLoginRateLimit = rateLimitRequestUrl.includes(AUTH_LOGIN);

        setError(isLoginRateLimit ? loginRateLimitMessage : registerRateLimitMessage);

        return {
          success: false,
          rateLimited: true,
          cooldownSeconds: isLoginRateLimit
            ? LOGIN_RATE_LIMIT_COOLDOWN_SECONDS
            : REGISTRATION_RATE_LIMIT_COOLDOWN_SECONDS,
        };
      }

      setError(getApiErrorMessage(error, genericErrorMessage));
      return {
        success: false,
      };
    } finally {
      setIsFetching(false);
    }
  };

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const refreshSessionContext = useCallback(async () => {
    await syncSessionContext();
  }, [syncSessionContext]);

  function initUserPackage(company: string | null): {
    company: string | undefined;
    document: string | undefined;
  } {
    const savingPackage = safeJsonParse<Partial<PackageProps>>(
      localStorage.getItem('nuxeoPackage') || '{}',
    );
    if (company) {
      if (company === savingPackage?.company) {
        dispatch(setUserPackage({ userPackage: savingPackage }));
        return {
          company: savingPackage.company,
          document: savingPackage.document ?? '',
        };
      } else {
        dispatch(setCompany({ company: company }));
        return { company: company, document: '' };
      }
    }
    return { company: undefined, document: undefined };
  }

  function safeJsonParse<T>(str: string) {
    try {
      const jsonValue: T = JSON.parse(str);
      return jsonValue;
    } catch {
      return undefined;
    }
  }

  //Определяем роут перехода
  //При наличии package идем на from
  //Иначе на profile
  function getNavigateTo(company: string | undefined, document: string | undefined, from: string) {
    return company?.length && document?.length ? from : DEFAULT_AUTHENTICATED_ROUTE;
  }

  const isLoggedIn = () => {
    return !!user;
  };

  const logout = () => {
    dispatch(setUserPackage({ userPackage: initialStatePackage }));
    authStorage.clearAuth();
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    setOrganisation(null);
    navigate('/login', { replace: true });
  };

  return (
    <UserContext.Provider
      value={{
        login,
        reg,
        user,
        token,
        refreshToken,
        organisation,
        refreshSessionContext,
        logout,
        isLoggedIn,
        canManageTeam: canManageOrganisationMembers(user?.role),
        isFetching,
        error,
        clearError,
      }}
    >
      {isReady ? children : null}
    </UserContext.Provider>
  );
};

const useUserContext = () => {
  const context = useContext<IUserContext>(UserContext as unknown as Context<IUserContext>);

  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

export { UserProvider, useUserContext };
